import WidgetBase from '../widget.base';
import Swiper from '../../lib/swipe';

/**
 * The hero slideshow widget (shown on the homepage).
 */
export default class HeroSlideshow extends WidgetBase {
    static selector = '[data-js-hero-slideshow]';

    /**
     * Constructor for this widget.
     * @param el The DOM element for the widget.
     */
    constructor(el) {

        // Boilerplate.
        super(el);

        // Find DOM elements within this widget instance.
        this.findElements(el);

        // Initialize instance variables.
        this.initializeState();

        // Initialize elements.
        this.initializeElements();

        // Setup event handlers.
        this.setupEventHandlers();

        // Setup automatic transitioning of the slides.
        this.setupAutoSlide();

        // Initialize swipe handling.
        this.setupSwipe(el);

    }

    /**
     * Sets up the handling of swipe events.
     * @param el The DOM element for the slideshow.
     */
    setupSwipe(el) {
        let swipe = new Swiper();
        swipe.init(el, this.handleSwipe.bind(this));
    }

    /**
     * Handles a swipe event.
     * @param data data for the swipe, such as the direction of the swipe (e.g., left or right).
     */
    handleSwipe(data) {
        let direction = data.direction;
        if (direction === 'left') {
            this.nextSlide();
        } else if (direction === 'right') {
            this.previousSlide();
        }
    }

    /**
     * Toggles the automatic transitioning of one slide to the next (i.e., this will
     * pause or reinitiate the transitioning).
     */
    toggleAutoSlide() {

        // Variables.
        let playClass = 'hero-slideshow__play-toggle--will-play',
            pauseClass = 'hero-slideshow__play-toggle--will-pause';

        // Toggle auto-play.
        this.isAutoPlaying = !this.isAutoPlaying;

        // Reinitiate or stop the timeout function.
        if (this.isAutoPlaying) {
            this.setupAutoSlide();
        } else {
            clearTimeout(this.autoSlideTimeoutId);
        }

        // Swap out CSS class (this will cause the button to change to either a play or a pause button).
        if (this.isAutoPlaying) {
            this.playToggleEl.classList.remove(playClass);
            this.playToggleEl.classList.add(pauseClass);
        } else {
            this.playToggleEl.classList.remove(pauseClass);
            this.playToggleEl.classList.add(playClass);
        }

    }

    /**
     * Setup automatic sliding from one slide to the next every few seconds.
     */
    setupAutoSlide() {
        this.autoSlideTimeoutId = setTimeout(this.nextSlide.bind(this), this.slideDuration);
    }

    /**
     * Navigates to a previous or subsequent slide.
     * @param direction The direction to navigate (-1 for previous slide, +1 for next slide).
     */
    advanceSlide(direction) {

        // Change slide index (wrap around to beginning or end if one is reached).
        this.pageIndex += direction;
        if (this.pageIndex < 0) {
            this.pageIndex = this.slideCount - 1;
        } else if (this.pageIndex >= this.slideCount) {
            this.pageIndex = 0;
        }

        // Update the pager button.
        this.updatePageIndicator();

        // Update the left offset (which will show the new slide).
        this.wrapperEl.style.left = '-' + (100 * this.pageIndex).toString() + 'vw';

        // Reset the automatic sliding (so it doesn't happen immediately after the slide just advanced).
        if (this.isAutoPlaying) {
            clearTimeout(this.autoSlideTimeoutId);
            this.setupAutoSlide();
        }

        // Set the controls to have the colors appropriate for the current slide.
        this.updateSpoofColorScheme();

    }

    /**
     * Navigates to the slide at the specified index.
     * @param index The index of the slide to navigate to.
     */
    goToSlide(index) {
        let slideIndexDiff = index - this.pageIndex;
        this.advanceSlide(slideIndexDiff);
    }

    /**
     * Navigate to the previous slide.
     */
    previousSlide() {
        this.advanceSlide(-1);
    }

    /**
     * Navigate to the next slide.
     */
    nextSlide() {
        this.advanceSlide(1);
    }

    /**
     * Updates the pager button to indicate the current slide being displayed.
     */
    updatePageIndicator() {
        let i;
        for (i = 0; i < this.pagerEls.length; i++) {
            this.pagerEls[i].classList.remove('active');
        }
        this.pagerEls[this.pageIndex].classList.add('active');
    }

    /**
     * Setup the event handlers (e.g., click events).
     */
    setupEventHandlers() {
        let i;
        for (i = 0; i < this.pagerEls.length; i++) {
            this.pagerEls[i].addEventListener('click', this.getPageClickHandler(i));
        }
        this.playToggleEl.addEventListener('click', this.toggleAutoSlide.bind(this));
    }

    /**
     * Returns the click handler for the pager button corresponding to the slide at the specified index.
     * @param index The slide index.
     * @returns {Function} The click handler.
     */
    getPageClickHandler(index) {
        let self = this;
        return function () {
            self.goToSlide.bind(self)(index);
        };
    }

    /**
     * Find the elements within this widget instance and store them for later.
     * @param el The main DOM element for this widget.
     */
    findElements(el) {
        this.wrapperEl = el.querySelector('[data-js-slideshow-wrapper]');
        this.slideEls = el.querySelectorAll('[data-js-slideshow-slide]');
        this.pagerEls = el.querySelectorAll('[data-js-slideshow-page]');
        this.playToggleEl = el.querySelector('[data-js-slideshow-play-toggle]');
        this.spoofSlideEl = el.querySelector('[data-js-slideshow-slide-spoof]');
    }

    /**
     * Updates the color scheme class on the spoofed version of the slide. This allows the
     * controls to match the color scheme of the current slide.
     */
    updateSpoofColorScheme() {
        let slideEl = this.slideEls[this.pageIndex],
            colorScheme = slideEl.getAttribute('data-js-color-scheme'),
            classList = this.spoofSlideEl.classList;
        classList.remove('hero-slideshow__slide--light');
        classList.remove('hero-slideshow__slide--dark');
        classList.add('hero-slideshow__slide--' + colorScheme);
    }

    /**
     * Initialize the instance variables for the widget.
     */
    initializeState() {

        // Is the slideshow automatically transitioning (if not, it's paused).
        this.isAutoPlaying = true;

        // The number of milliseconds to spend on each slide before automatically advancing
        // to the next one.
        this.slideDuration = 7000;

        // By default, we're viewing the first slide.
        this.pageIndex = 0;

        // Remember the number of slides.
        this.slideCount = this.slideEls.length;

    }

    /**
     * Initialize the DOM elements.
     */
    initializeElements() {

        // Set the left so that the CSS transition works on the first page change.
        this.wrapperEl.style.left = "0";

        // Set the initial color scheme for the first slide.
        this.updateSpoofColorScheme();

    }

}
